import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';
import { Mail, Phone, RotateCcw, Clock, Receipt } from 'lucide-react';

const RefundPage = () => {
  usePageTitle(`Refund & Cancellation Policy | ${BRAND_INFO.name}`,
    `${BRAND_INFO.name} 30-day refund policy and clear cancellation steps. Cancel anytime, get a refund within 30 days of your charge.`);

  return (
    <>
      <Header />
      <main className="pt-28 lg:pt-36 pb-24 bg-cream-100">
        <div className="container-page">
          <p className="eyebrow">Legal</p>
          <h1 className="mt-4 font-display text-display-lg text-ink-900">Refund &amp; Cancellation Policy</h1>
          <p className="mt-4 text-ink-600">Last updated: July 2025</p>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {[
              { icon: Clock,    title: '30 days',                 body: 'Request a full refund within 30 days of your most recent charge — if your panel was not yet drawn.' },
              { icon: RotateCcw, title: 'Cancel any time',         body: 'Stop recurring panels from inside your account or by contacting our team.' },
              { icon: Receipt,  title: '5 – 10 business days',     body: 'Approved refunds return to the original payment method, typically inside two weeks.' },
            ].map((c, i) => (
              <div key={i} className="p-6 rounded-3xl bg-cream-50 border border-cream-200">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-brick-500 text-white">
                  <c.icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-2xl text-ink-900">{c.title}</h3>
                <p className="mt-2 text-ink-600">{c.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 space-y-10 text-ink-700 leading-relaxed">
            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">Overview</h2>
              <p className="mt-3">{BRAND_INFO.name} is a digital health information platform operated by {CONTACT_INFO.corporateName}. We want every customer to feel confident in their order. If you change your mind before a partner lab has begun fulfilment, our policy is simple and written.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">Refund eligibility</h2>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li><strong>Order placed but draw not yet completed:</strong> a full refund is available if you contact us within thirty (30) days of the charge. Once you have walked into a partner draw center and your blood has been collected, the partner lab&rsquo;s fulfilment fee becomes non-refundable, but the {BRAND_INFO.name} platform fee for that order may still be refunded at our discretion.</li>
                <li><strong>Recurring panels:</strong> a refund of your most recent recurring charge is available within thirty (30) days, provided that draw has not yet been completed. Future cadences may be cancelled at any time to prevent further charges.</li>
                <li><strong>Duplicate or accidental charges:</strong> always refundable. Contact us and we will reverse the charge promptly.</li>
                <li>Refunds are returned to the original payment method, typically within five to ten (5–10) business days after the refund is approved. Your bank may take additional time to post the credit.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">How to request a refund</h2>
              <ol className="list-decimal pl-5 mt-3 space-y-2">
                <li>Email {CONTACT_INFO.email} from the email address associated with your account, or call {CONTACT_INFO.phone} during business hours.</li>
                <li>Include the date of the charge, the amount, and a brief reason (this helps us improve the product).</li>
                <li>You will receive a written confirmation within one business day. The refund itself usually posts within 5–10 business days.</li>
              </ol>
            </section>

            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">How to cancel a recurring panel</h2>
              <p className="mt-3">You may cancel any recurring panel at any time, with no penalty:</p>
              <ul className="list-disc pl-5 mt-3 space-y-2">
                <li><strong>In-app:</strong> open Account &rarr; Billing &rarr; Cancel.</li>
                <li><strong>Email:</strong> {CONTACT_INFO.email}.</li>
                <li><strong>Phone:</strong> {CONTACT_INFO.phone}.</li>
                <li><strong>Mail:</strong> {CONTACT_INFO.corporateName}, {CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}.</li>
              </ul>
              <p className="mt-3">Cancellation stops future renewal charges. You retain access to every lab result already on your dashboard — a cancelled subscription does not erase your timeline.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">Chargebacks &amp; disputes</h2>
              <p className="mt-3">If you see a charge you do not recognize on a statement showing the descriptor {BRAND_INFO.billingDescriptor}, please contact us before initiating a chargeback. The vast majority of disputes are resolved within one to two business days, and a written refund leaves a cleaner paper trail than a card-network reversal.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">No physical product</h2>
              <p className="mt-3">Because {BRAND_INFO.name} is a digital service — we deliver a physician-signed lab requisition and a dashboard, not a kit — there is no return shipping, no return-shipping label, and no item to physically return. Service is delivered electronically and made available immediately upon successful payment via {BRAND_INFO.domain}.</p>
            </section>

            <section>
              <h2 className="font-display text-2xl lg:text-3xl text-ink-900">Changes</h2>
              <p className="mt-3">We may update this policy from time to time. The &ldquo;Last updated&rdquo; date above reflects the latest revision. The version in effect at the time of your charge governs that charge.</p>
            </section>
          </div>

          <div className="mt-14 p-8 rounded-3xl bg-charcoal-900 text-cream-50 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-display text-2xl">Need to talk to a human?</h3>
              <p className="mt-2 text-cream-200/80">Our team replies to every refund email — usually within one business day.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={`mailto:${CONTACT_INFO.email}`} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brick-500 hover:bg-brick-600 text-white">
                <Mail className="w-4 h-4" /> Email support
              </a>
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-cream-200/60 text-cream-50 hover:bg-cream-50 hover:text-ink-900 transition-all">
                <Phone className="w-4 h-4" /> {CONTACT_INFO.phone}
              </a>
            </div>
          </div>

          <p className="mt-10 text-sm text-ink-500">
            See also: <Link to="/terms" className="underline">Terms &amp; Conditions</Link>,{' '}
            <Link to="/subscription" className="underline">Subscription Disclosure</Link>,{' '}
            <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default RefundPage;
